import { useCallback, useEffect, useState } from 'react'
import {
    Box, Button, Card, CardHeader, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, IconButton, TextField, Tooltip
} from '@mui/material'
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid'
import EditIcon from '@mui/icons-material/Edit'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import CachedIcon from '@mui/icons-material/Cached'
import AddIcon from '@mui/icons-material/Add'
import toast from 'react-hot-toast'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import { AsyncSelect } from 'src/components/forms'
import { useForm } from 'react-hook-form'
import {
    HttpObjetosSeguridadABM,
    ObjetoSeguridadPostDTO,
    ObjetoSeguridadViewDTO
} from '../api/httpObjetosSeguridadABM'

function TableObjetosSeguridad() {
    const { askConfirmation } = useConfirmationDialog()
    const [objetos, setObjetos] = useState<ObjetoSeguridadViewDTO[]>([])
    const [loading, setLoading] = useState(false)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingObjeto, setEditingObjeto] = useState<ObjetoSeguridadViewDTO | null>(null)

    const cargar = useCallback(() => {
        setLoading(true)
        HttpObjetosSeguridadABM.getObjetos()
            .then(setObjetos)
            .catch(() => toast.error('Error al cargar objetos'))
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => { cargar() }, [cargar])

    const columnas: GridColDef[] = [
        { headerName: 'Código', field: 'codigo', flex: 1.5 },
        { headerName: 'Descripción', field: 'descripcion', flex: 2 },
        { headerName: 'Tipo', field: 'descTipoObjetoSeguridad', flex: 1 },
        { headerName: 'Cód. Link', field: 'codLink', flex: 1.2,
            renderCell: ({ value }) => value || <Chip label='—' size='small' variant='outlined' />
        },
        { headerName: 'Cód. Ruta', field: 'codRuta', flex: 1.2,
            renderCell: ({ value }) => value || <Chip label='—' size='small' variant='outlined' />
        },
        { headerName: 'Observaciones', field: 'observaciones', flex: 2 },
        {
            headerName: 'Activo', field: 'bitActivo', flex: 0.5,
            renderCell: ({ value }) => (
                <Chip label={value ? 'Sí' : 'No'} size='small' color={value ? 'success' : 'default'} />
            )
        },
        {
            field: 'actions', type: 'actions', width: 80,
            getActions: ({ row }) => [
                <GridActionsCellItem
                    key='edit'
                    icon={<Tooltip title='Editar'><EditIcon color='info' /></Tooltip>}
                    label='Editar'
                    onClick={() => { setEditingObjeto(row); setDialogOpen(true) }}
                />,
                <GridActionsCellItem
                    key='delete'
                    icon={<Tooltip title='Inactivar'><PersonRemoveIcon color='error' /></Tooltip>}
                    label='Inactivar'
                    onClick={() => askConfirmation(() => {
                        const promise = HttpObjetosSeguridadABM.inactivar(row.id)
                        toast.promise(promise, {
                            success: 'Inactivado', error: 'Error', loading: 'Inactivando'
                        }).then(cargar)
                    })}
                />
            ]
        }
    ]

    const handleNuevo = () => {
        setEditingObjeto(null)
        setDialogOpen(true)
    }

    const handleClose = () => {
        setDialogOpen(false)
        setEditingObjeto(null)
    }

    const handleSaved = () => {
        handleClose()
        cargar()
    }

    return (
        <Card>
            <CardHeader
                title='Objetos de Seguridad'
                action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant='contained' size='small' startIcon={<AddIcon />} onClick={handleNuevo}>
                            Nuevo
                        </Button>
                        <IconButton disabled={loading} onClick={cargar}>
                            <CachedIcon />
                        </IconButton>
                    </Box>
                }
            />
            <DataGrid
                rows={objetos}
                columns={columnas}
                loading={loading}
                autoHeight
                density='compact'
                disableColumnMenu
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                pageSizeOptions={[25, 50, 100]}
            />
            <Dialog open={dialogOpen} fullWidth maxWidth='sm' keepMounted={false}>
                {dialogOpen && (
                    <DialogObjetoSeguridad
                        objeto={editingObjeto}
                        onClose={handleClose}
                        onSaved={handleSaved}
                    />
                )}
            </Dialog>
        </Card>
    )
}

function DialogObjetoSeguridad({
    objeto, onClose, onSaved
}: {
    objeto: ObjetoSeguridadViewDTO | null
    onClose: () => void
    onSaved: () => void
}) {
    const isEdit = !!objeto
    const { control, handleSubmit, register, formState: { errors } } = useForm<ObjetoSeguridadPostDTO>({
        defaultValues: objeto ? {
            codigo: objeto.codigo,
            descripcion: objeto.descripcion,
            sinCodTipoObjetoSeguridad: objeto.sinCodTipoObjetoSeguridad,
            codLink: objeto.codLink,
            codRuta: objeto.codRuta,
            observaciones: objeto.observaciones
        } : {
            codigo: '',
            descripcion: '',
            sinCodTipoObjetoSeguridad: undefined as any,
            codLink: null,
            codRuta: null,
            observaciones: ''
        }
    })

    const onSubmit = (data: ObjetoSeguridadPostDTO) => {
        const promise = isEdit
            ? HttpObjetosSeguridadABM.putObjeto(objeto!.id, data)
            : HttpObjetosSeguridadABM.postObjeto(data)

        toast.promise(promise, {
            loading: isEdit ? 'Actualizando...' : 'Creando...',
            success: isEdit ? 'Actualizado' : 'Creado',
            error: 'Error'
        }).then(onSaved)
    }

    return (
        <>
            <DialogTitle>{isEdit ? 'Editar' : 'Nuevo'} Objeto de Seguridad</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} pt={2}>
                    <Grid item xs={12}>
                        <TextField
                            {...register('codigo', { required: 'Requerido' })}
                            label='Código (clave única)'
                            fullWidth
                            size='small'
                            error={!!errors.codigo}
                            helperText={errors.codigo?.message}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            {...register('descripcion', { required: 'Requerido' })}
                            label='Descripción'
                            fullWidth
                            size='small'
                            error={!!errors.descripcion}
                            helperText={errors.descripcion?.message}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <AsyncSelect
                            control={control}
                            name='sinCodTipoObjetoSeguridad'
                            label='Tipo'
                            loadOptions={() => HttpObjetosSeguridadABM.getTipos()}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            {...register('codLink')}
                            label='Cód. Link'
                            fullWidth
                            size='small'
                            placeholder='Ej: LinkTablaUsuarios'
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            {...register('codRuta')}
                            label='Cód. Ruta'
                            fullWidth
                            size='small'
                            placeholder='Ej: RutaAdminMaster'
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            {...register('observaciones')}
                            label='Observaciones'
                            fullWidth
                            size='small'
                            multiline
                            rows={2}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant='contained' onClick={handleSubmit(onSubmit)}>
                    {isEdit ? 'Guardar' : 'Crear'}
                </Button>
            </DialogActions>
        </>
    )
}

export default TableObjetosSeguridad
